import { useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { AppShell } from '@/components/layout/AppShell'
import { AlertItem } from '@/components/alerts/AlertItem'
import { OrderTicketModal } from '@/components/orders/OrderTicketModal'
import { useAlertList, useMarkAlertRead, useAcceptAlert } from '@/lib/useAlerts'
import type { Alert, AlertListFilters } from '@/types/alert'

type FilterTab = 'all' | 'unread' | 'critical' | 'reminder'

const VALID_TABS: readonly FilterTab[] = ['all', 'unread', 'critical', 'reminder']

function parseTab(raw: string | null): FilterTab {
  return (VALID_TABS as readonly string[]).includes(raw ?? '') ? (raw as FilterTab) : 'unread'
}

const TAB_TO_FILTERS: Record<FilterTab, AlertListFilters> = {
  all: {},
  unread: { unread_only: true },
  critical: { severity: 'critical' },
  reminder: { category: 'reminder' },
}

const TABS: { id: FilterTab; label: string }[] = [
  { id: 'unread', label: 'Ungelesen' },
  { id: 'all', label: 'Alle' },
  { id: 'critical', label: 'Kritisch' },
  { id: 'reminder', label: 'Reminder' },
]

const SEVERITY_ORDER: Record<string, number> = {
  critical: 0,
  warning: 1,
  info: 2,
}

function sortBySeverity(items: Alert[]): Alert[] {
  return [...items].sort((a, b) => {
    const sevDiff = (SEVERITY_ORDER[a.severity] ?? 99) - (SEVERITY_ORDER[b.severity] ?? 99)
    if (sevDiff !== 0) return sevDiff
    return new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
  })
}

export function AlertsPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const tab = parseTab(searchParams.get('tab'))
  const setTab = (next: FilterTab) => {
    const params = new URLSearchParams(searchParams)
    if (next === 'unread') {
      params.delete('tab')
    } else {
      params.set('tab', next)
    }
    setSearchParams(params, { replace: false })
  }
  const [ticketAlert, setTicketAlert] = useState<Alert | null>(null)
  const filters = TAB_TO_FILTERS[tab]
  const query = useAlertList(filters)
  const navigate = useNavigate()
  const markRead = useMarkAlertRead()
  const accept = useAcceptAlert()

  const handleRebalance = (_alert: Alert) => {
    navigate('/rebalance')
  }

  const handleAccept = (alert: Alert) => {
    accept.mutate(alert.id)
  }

  const handleClick = (alert: Alert) => {
    if (alert.read_at === null) {
      markRead.mutate(alert.id)
    }
  }

  const handleOrderTicket = (alert: Alert) => {
    setTicketAlert(alert)
  }

  const items = sortBySeverity(query.data?.items ?? [])

  return (
    <AppShell>
      <article className="fade-in max-w-4xl">
        <header className="flex flex-col items-stretch gap-4 md:flex-row md:items-end md:justify-between md:gap-6">
          <div>
            <p className="tracked-caps text-[10px] text-faint">Benachrichtigungen</p>
            <h2 className="mt-2 text-2xl text-ink">Alerts</h2>
            <p className="mt-1 font-mono text-xs text-muted">
              {query.data?.unread_count ?? 0} ungelesen · {query.data?.total ?? 0} im Filter
            </p>
          </div>
          <nav className="flex flex-wrap gap-1" role="tablist" aria-label="Alert-Filter">
            {TABS.map((t) => (
              <button
                key={t.id}
                type="button"
                role="tab"
                aria-selected={tab === t.id}
                onClick={() => setTab(t.id)}
                className={[
                  'tracked-caps border px-4 py-2 text-[11px] transition-colors',
                  tab === t.id
                    ? 'border-bernstein text-bernstein'
                    : 'border-hairline text-muted hover:border-bernstein hover:text-bernstein',
                ].join(' ')}
              >
                {t.label}
              </button>
            ))}
          </nav>
        </header>

        <div className="mt-10">
          {query.isLoading ? (
            <p className="font-mono text-sm text-muted">Lade Alerts…</p>
          ) : query.isError ? (
            <div role="alert" className="border border-loss/40 bg-loss/5 px-4 py-3">
              <p className="tracked-caps text-[10px] text-loss">Fehler</p>
              <p className="mt-2 font-mono text-sm text-ink">
                Alerts konnten nicht geladen werden.
              </p>
              <p className="mt-1 font-mono text-xs text-muted">{query.error.message}</p>
            </div>
          ) : items.length === 0 ? (
            <div className="border border-hairline bg-panel px-6 py-12 text-center">
              <p className="tracked-caps text-[10px] text-faint">Leer</p>
              <p className="mt-3 text-sm text-muted">
                {tab === 'unread' ? 'Keine ungelesenen Alerts.' : 'Keine Alerts im Filter.'}
              </p>
            </div>
          ) : (
            <div className="flex flex-col">
              {items.map((alert) => (
                <AlertItem
                  key={alert.id}
                  alert={alert}
                  onClick={handleClick}
                  onOrderTicket={handleOrderTicket}
                  onRebalance={handleRebalance}
                  onAccept={handleAccept}
                />
              ))}
            </div>
          )}
        </div>
      </article>
      <OrderTicketModal
        open={ticketAlert !== null}
        onClose={() => setTicketAlert(null)}
        positionId={ticketAlert?.related_position_id ?? null}
        alertId={ticketAlert?.id ?? null}
        positionName={ticketAlert?.title}
      />
    </AppShell>
  )
}
